import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useEffect, useState } from 'react';
import { useContextOrThrow } from '@elfsight/lib-utils';
import { noop } from 'es-toolkit';
import type {
  PaymentProcessingChangeHandler,
  PaymentSessionStatus
} from '../common';

type SessionMetadata = {
  sessionId: string | undefined;
  setSessionId: Dispatch<SetStateAction<string | undefined>>;
  sessionStatus: PaymentSessionStatus;
  setSessionStatus: Dispatch<SetStateAction<PaymentSessionStatus>>;
  isPaymentInProcess: boolean;
  setIsPaymentInProcess: Dispatch<SetStateAction<boolean>>;
};

const SessionMetadataContext = createContext<SessionMetadata | null>(null);

export function SessionMetadataProvider({
  onPaymentProcessing = noop,
  children
}: PropsWithChildren<{
  onPaymentProcessing?: PaymentProcessingChangeHandler;
}>) {
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessionStatus, setSessionStatus] =
    useState<PaymentSessionStatus>('unset');
  const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);

  useEffect(() => {
    onPaymentProcessing(isPaymentInProcess);
  }, [isPaymentInProcess, onPaymentProcessing]);

  return (
    <SessionMetadataContext.Provider
      value={{
        sessionId,
        setSessionId,
        sessionStatus,
        setSessionStatus,
        isPaymentInProcess,
        setIsPaymentInProcess
      }}
    >
      {children}
    </SessionMetadataContext.Provider>
  );
}

export function useSessionMetadata() {
  return useContextOrThrow(SessionMetadataContext, 'SessionMetadata');
}
